import { Injectable, inject } from '@angular/core';
import {
  ApiDependencyProviderService,
  CollectionRequest,
  CollectionResponse,
  HttpParams,
} from '@web/common';
import { blank, Guid } from '@web/utils';
import { map, Observable } from 'rxjs';
import { AdminBoxSaleCollectionItemResponse } from '../responses/admin-box-sale-collection-item.response';
import { AdminBoxSaleDetailsResponse } from '../responses/admin-box-sale-details.response';
import { AdminBoxSaleUpdateRequest } from '../requests/admin-box-sale-update.request';
import { AdminBoxSaleApproveStatusResponse } from '../responses/admin-box-sale-approve-status.response';

@Injectable({
  providedIn: 'root',
})
export class AdminBoxSaleApiService {
  private readonly provider = inject(ApiDependencyProviderService);

  public fetchCollection(
    request: CollectionRequest,
  ): Observable<CollectionResponse<AdminBoxSaleCollectionItemResponse>> {
    const path = 'admin/box/sale';
    const url = this.provider.httpUrlFactory.withQueryParams(
      this.hostUrl(),
      path,
      this.provider.httpHelper.collectionRequestToQueryParams(request),
    );
    const context = this.provider.httpContextBuilder
      .skipByHttpErrorInterceptor()
      .build();

    return this.provider.http
      .get(url, { context })
      .pipe(
        map(
          this.provider.httpHelper.bodyToCollectionResponse(
            AdminBoxSaleCollectionItemResponse,
          ),
        ),
      );
  }

  public fetchDetailsById(id: Guid): Observable<AdminBoxSaleDetailsResponse> {
    const path = `admin/box/sale/${HttpParams.Id}`;
    const url = this.provider.httpUrlFactory.withParams(this.hostUrl(), path, {
      [HttpParams.Id]: id,
    });
    const context = this.provider.httpContextBuilder
      .skipByHttpErrorInterceptor()
      .skipByLoadingInterceptor()
      .build();

    return this.provider.http
      .get(url, { context })
      .pipe(
        map(
          this.provider.httpHelper.bodyToResponse(AdminBoxSaleDetailsResponse),
        ),
      );
  }

  public fetchApproveStatusById(
    id: Guid,
  ): Observable<AdminBoxSaleApproveStatusResponse> {
    const path = `admin/box/sale/${HttpParams.Id}/approve/status`;
    const url = this.provider.httpUrlFactory.withParams(this.hostUrl(), path, {
      [HttpParams.Id]: id,
    });
    const context = this.provider.httpContextBuilder
      .skipByHttpErrorInterceptor()
      .skipByLoadingInterceptor()
      .build();

    return this.provider.http
      .get(url, { context })
      .pipe(
        map(
          this.provider.httpHelper.bodyToResponse(
            AdminBoxSaleApproveStatusResponse,
          ),
        ),
      );
  }

  public update(
    id: Guid,
    request: AdminBoxSaleUpdateRequest,
  ): Observable<void> {
    const path = `admin/box/sale/${HttpParams.Id}`;
    const url = this.provider.httpUrlFactory.withParams(this.hostUrl(), path, {
      [HttpParams.Id]: id,
    });
    const context = this.provider.httpContextBuilder
      .skipByHttpErrorInterceptor()
      .build();
    const body = this.provider.httpHelper.requestToBody(
      request,
      AdminBoxSaleUpdateRequest,
    );

    return this.provider.http.put(url, body, { context }).pipe(blank());
  }

  public removeById(id: Guid): Observable<void> {
    const path = `admin/box/sale/${HttpParams.Id}`;
    const url = this.provider.httpUrlFactory.withParams(this.hostUrl(), path, {
      [HttpParams.Id]: id,
    });

    return this.provider.http.delete(url).pipe(blank());
  }

  public publicContentById(id: Guid): Observable<void> {
    const path = `admin/box/sale/${HttpParams.Id}/content/public`;
    const url = this.provider.httpUrlFactory.withParams(this.hostUrl(), path, {
      [HttpParams.Id]: id,
    });

    return this.provider.http.post(url, {}).pipe(blank());
  }

  public hideContentById(id: Guid): Observable<void> {
    const path = `admin/box/sale/${HttpParams.Id}/content/hide`;
    const url = this.provider.httpUrlFactory.withParams(this.hostUrl(), path, {
      [HttpParams.Id]: id,
    });

    return this.provider.http.post(url, {}).pipe(blank());
  }

  public approveById(id: Guid): Observable<void> {
    const path = `admin/box/sale/${HttpParams.Id}/approve`;
    const url = this.provider.httpUrlFactory.withParams(this.hostUrl(), path, {
      [HttpParams.Id]: id,
    });

    return this.provider.http.post(url, {}).pipe(blank());
  }

  public archiveById(id: Guid): Observable<void> {
    const path = `admin/box/sale/${HttpParams.Id}/archive`;
    const url = this.provider.httpUrlFactory.withParams(this.hostUrl(), path, {
      [HttpParams.Id]: id,
    });

    return this.provider.http.post(url, {}).pipe(blank());
  }

  private hostUrl(): string {
    return this.provider.environment.ApiHostUrl.Shop;
  }
}
